import type { ComputedRef, Ref } from 'vue';
import { storeToRefs } from 'pinia';
import { createAccountShipment } from '@gpx-spa/queries';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import type { useShipmentFormSave } from '@/composables/useShipmentFormSave';
import type { useShipmentFormValidation } from '@/composables/useShipmentFormValidation';

type FormSave = ReturnType<typeof useShipmentFormSave>;
type FormValidation = ReturnType<typeof useShipmentFormValidation>;

export function useShipmentFormCommit(options: {
  accountId: Ref<number> | ComputedRef<number>;
  formSave: FormSave;
  validation: FormValidation;
}) {
  const formStore = useShipmentFormStore();
  const {
    shipmentId,
    generalName,
    generalCarrier,
    generalNotes,
    generalAssetValueCents,
    selectedDeviceIds,
  } = storeToRefs(formStore);

  const createShipmentMutation = createAccountShipment();

  async function ensureDraft(): Promise<number> {
    if (shipmentId.value != null) {
      return shipmentId.value;
    }

    const payload = options.validation.normalizeMetadataPayload();
    const created = await createShipmentMutation.mutateAsync({
      accountId: options.accountId.value,
      payload,
    });

    shipmentId.value = created.id;
    formStore.markMetadataPersisted(
      payload.name,
      payload.carrier,
      payload.notes,
      payload.asset_value,
    );
    return created.id;
  }

  async function commitDevicesIfDirty(): Promise<void> {
    const ids = selectedDeviceIds.value;
    if (ids.length === 0) {
      throw new Error('Tracker is required');
    }
    if (!formStore.haveDeviceIdsChangedSincePersisted(ids)) {
      return;
    }
    await options.formSave.assignDevices(ids);
  }

  async function commitMetadataIfDirty(): Promise<void> {
    const payload = options.validation.normalizeMetadataPayload();
    if (
      !formStore.haveMetadataChangedSincePersisted(
        generalName.value,
        generalCarrier.value,
        generalNotes.value,
        generalAssetValueCents.value,
      )
    ) {
      return;
    }
    await options.formSave.saveMetadata(payload);
  }

  async function ensureDraftWithDevices(): Promise<number> {
    const id = await ensureDraft();
    await commitDevicesIfDirty();
    return id;
  }

  return {
    ensureDraft,
    commitDevicesIfDirty,
    commitMetadataIfDirty,
    ensureDraftWithDevices,
  };
}
